// A sketch: an editable model in the browser. The diagram shows what changed from the model it
// started from; drag from a card's dot to another card to relate them, and edit details in the
// inspector or as JSON.
import { addEntity, addRelationship, diffModels, removeEntity, removeProperty, removeRelationship, validateDocument, type ModelSpec } from '@modelviewer/core';
import type { DockviewPanelApi } from 'dockview-react';
import { Braces, Columns2, Copy, Download, FileCode2, FolderOpen, GitCompareArrows, Loader2, Plus, Redo2, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { create } from 'zustand';
import { Canvas, Divider, ToolButton, type CanvasApi, type CanvasSelection, type CanvasTarget } from '../diagram/Canvas';
import { useCardFonts } from '../diagram/DiagramView';
import { buildGraph, type GraphOptions } from '../diagram/graph';
import { measureText } from '../diagram/measure';
import { useDiagramSettings } from '../diagram/settings';
import { layoutInput, useLayout } from '../diagram/useLayout';
import { buildDiffGraph } from '../diff/diffGraph';
import type { SketchRecord } from '../lib/db';
import { openCompare, openSketch, openSourceFile } from '../state/dock';
import { baseLabel, baseRefOf, duplicateSketch, editSketch, getSketch, layoutKeyFor, redoSketch, undoSketch, useSketchHistory } from '../state/sketches';
import { toast } from '../state/toast';
import { useModel } from '../state/useModel';
import { refKey, select, setActiveRef, sketchRef, useWorkspace } from '../state/workspace';
import { exportSketch } from './files';
import { SketchJson } from './SketchJson';

/** Asks the inspector to focus the name field of a new entity, once. */
export const useNameFocus = create<{ key: string | null; nonce: number }>(() => ({ key: null, nonce: 0 }));

function isTyping(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('input, textarea, select, [contenteditable="true"], .cm-editor, [cmdk-root]');
}

type Rect = { x: number; y: number; width: number; height: number };

/**
 * Where to put a new card (about 220 by 110): the free spot closest to the middle of the visible
 * area, not overlapping other cards and away from the toolbar. Outside the view only if it is full.
 */
export function freeSpot(view: Rect, taken: Rect[]): { x: number; y: number } {
  const w = 220;
  const h = 110;
  const gap = 20;
  const free = (x: number, y: number) => taken.every((r) => x + w + gap <= r.x || x >= r.x + r.width + gap || y + h + gap <= r.y || y >= r.y + r.height + gap);
  const cx = view.x + view.width / 2 - w / 2;
  const cy = view.y + view.height / 2 - h / 2;
  const step = 30;
  let best: { x: number; y: number; d: number } | null = null;
  // Keep clear of the toolbar (top) and the controls (bottom) of the visible area.
  const top = view.y + view.height * 0.12;
  const bottom = view.y + view.height * 0.88 - h;
  for (let y = top; y <= bottom; y += step) {
    for (let x = view.x + 20; x <= view.x + view.width - w - 20; x += step) {
      if (!free(x, y)) continue;
      const d = (x - cx) ** 2 + (y - cy) ** 2;
      if (!best || d < best.d) best = { x, y, d };
    }
  }
  if (best) return { x: Math.round(best.x), y: Math.round(best.y) };
  // The view is full: below everything.
  const lowest = Math.max(...taken.map((r) => r.y + r.height), cy);
  return { x: Math.round(cx), y: Math.round(lowest + 60) };
}

function freeEntityName(model: ModelSpec): string {
  let name = 'Entity';
  for (let i = 2; model.entities[name]; i++) name = `Entity${i}`;
  return name;
}

/** Deletes what is selected in a sketch: an entity, a property or a relationship. */
export function deleteSelection(sketchId: string): void {
  const sketch = getSketch(sketchId);
  const selection = useWorkspace.getState().selection;
  if (!sketch || !selection || selection.ref.sketchId !== sketchId) return;
  const v = selection.value;
  const ref = sketchRef(sketch);
  if (v.kind === 'entity') {
    if (v.key.includes('.') && !sketch.doc.model.entities[v.key]) return toast('Owned types are removed from their owner, in the inspector.');
    if (editSketch(sketchId, (d) => removeEntity(d, v.key))) select(ref, null);
  } else if (v.kind === 'property') {
    const parts = v.property.split('.');
    const name = parts.pop()!;
    const path = [v.entity, ...parts].join('.');
    if (editSketch(sketchId, (d) => removeProperty(d, path, name))) select(ref, { kind: 'entity', key: v.entity });
  } else if (editSketch(sketchId, (d) => removeRelationship(d, v.index))) {
    select(ref, null);
  }
}

export function SketchView({ sketchId, api }: { sketchId: string; api: DockviewPanelApi }) {
  const sketch = useWorkspace((s) => s.sketches[sketchId]);
  if (!sketch) return <div className="flex h-full items-center justify-center p-6 text-muted">This sketch was deleted.</div>;
  return <SketchEditor sketch={sketch} api={api} />;
}

function SketchEditor({ sketch, api }: { sketch: SketchRecord; api: DockviewPanelApi }) {
  const id = sketch.id;
  const doc = sketch.doc;
  const ref = useMemo(() => sketchRef(sketch), [sketch.id, sketch.setKey]);
  const baseRef = useMemo(() => baseRefOf(sketch), [sketch.base]);
  const { model: base, loading: baseLoading } = useModel(baseRef);
  const settings = useDiagramSettings();
  const source = useWorkspace((s) => s.source);
  const history = useSketchHistory((s) => s[id]);
  const fontsReady = useCardFonts();
  const [active, setActive] = useState(api.isActive);
  const [reveal, setReveal] = useState<{ id: string; nonce: number; focus?: boolean; deletion?: boolean } | null>(null);
  // In focus the focused card shows every property, whatever the property mode.
  const [focused, setFocused] = useState(false);
  const canvas = useRef<CanvasApi | null>(null);

  useEffect(() => {
    const d = api.onDidActiveChange((e) => setActive(e.isActive));
    return () => d.dispose();
  }, [api]);
  useEffect(() => api.setTitle(doc.title), [api, doc.title]);
  useEffect(() => {
    if (active) setActiveRef(ref);
  }, [active, ref]);

  const baseModel = base?.result.doc.model ?? null;
  const diff = useMemo(() => (baseModel ? diffModels(baseModel, doc.model, { renames: doc.renames }) : null), [baseModel, doc]);
  const showChanges = settings.sketchChanges && !!baseModel && !!diff;
  const properties = focused ? 'all' : settings.properties;
  const options = useMemo<GraphOptions>(() => ({ properties, collapseJoins: settings.collapseJoins, measure: measureText }), [properties, settings.collapseJoins]);
  const graph = useMemo(() => {
    if (!fontsReady || (baseRef && baseLoading)) return null;
    return showChanges ? buildDiffGraph({ before: baseModel!, after: doc.model, diff: diff!, options }) : buildGraph(doc.model, options);
  }, [fontsReady, baseRef, baseLoading, showChanges, baseModel, doc.model, diff, options]);
  const input = useMemo(() => (graph ? layoutInput(graph) : null), [graph]);
  const layoutKey = source ? layoutKeyFor(source.id, ref, settings.properties) : null;
  const { positions, save, relayout } = useLayout(layoutKey, input);
  const errors = useMemo(() => validateDocument(doc).issues.filter((i) => i.severity === 'error').length, [doc]);

  const selection = useWorkspace((s) => s.selection);
  const selected = useMemo<CanvasSelection>(() => {
    const none = { node: null, row: null, edge: null };
    if (!selection || !graph) return none;
    const key = refKey(selection.ref);
    const live = key === refKey(ref);
    if (!live && !(baseRef && key === refKey(baseRef))) return none;
    const nodeOf = (entity: string) => graph.nodes.find((n) => (live ? n.diff !== 'removed' : n.diff === 'removed') && n.key === entity)?.id ?? null;
    const v = selection.value;
    if (v.kind === 'entity') return { ...none, node: nodeOf(v.key) };
    if (v.kind === 'property') {
      const node = nodeOf(v.entity);
      return { ...none, row: node ? `${node}::${v.property}` : null };
    }
    return { ...none, edge: live ? `rel:${v.index}` : `was:rel:${v.index}` };
  }, [selection, graph, ref, baseRef]);

  const onSelect = useCallback(
    (target: CanvasTarget | null) => {
      if (!target) return select(ref, null);
      if (target.kind === 'node') return select(target.node.diff === 'removed' && baseRef ? baseRef : ref, { kind: 'entity', key: target.node.key });
      if (target.kind === 'row') {
        if (target.row.diff === 'removed' && baseRef) {
          return select(baseRef, { kind: 'property', entity: target.node.diff === 'removed' ? target.node.key : (target.node.previousKey ?? target.node.key), property: target.row.id });
        }
        return select(ref, { kind: 'property', entity: target.node.key, property: target.row.id });
      }
      if (target.edge.relationship === null) return;
      select(target.edge.diff === 'removed' && baseRef ? baseRef : ref, { kind: 'relationship', index: target.edge.relationship });
    },
    [ref, baseRef],
  );

  const onConnect = useCallback(
    (from: { key: string }, to: { key: string }) => {
      if (editSketch(id, (d) => addRelationship(d, { dependent: from.key, principal: to.key }))) {
        select(ref, { kind: 'relationship', index: getSketch(id)!.doc.model.relationships.length - 1 });
      }
    },
    [id, ref],
  );

  const addNewEntity = useCallback(() => {
    const name = freeEntityName(getSketch(id)!.doc.model);
    const view = canvas.current?.visible();
    if (view && graph && positions) {
      const taken = graph.nodes.filter((n) => positions[n.id]).map((n) => ({ ...positions[n.id]!, width: n.width, height: n.height }));
      const spot = freeSpot(view, taken);
      save({ [name]: spot });
      // No room in view: it went below the others, so show everything.
      const outside = spot.y > view.y + view.height - 110 || spot.x > view.x + view.width - 220;
      if (outside) window.setTimeout(() => canvas.current?.fitAll(), 80);
    }
    if (editSketch(id, (d) => addEntity(d, name))) {
      select(ref, { kind: 'entity', key: name });
      useNameFocus.setState({ key: name, nonce: Date.now() });
    }
  }, [id, ref, save, graph, positions]);

  const readOnly = !!sketch.origin;

  // Keys: undo, redo, delete. Text fields and the JSON editor keep their own.
  useEffect(() => {
    if (!active || readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || isTyping(e.target)) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoSketch(id);
      } else if (mod && (k === 'y' || (k === 'z' && e.shiftKey))) {
        e.preventDefault();
        redoSketch(id);
      } else if (!mod && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        deleteSelection(id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, id, readOnly]);

  // Center on an entity when the explorer or command palette asks for it.
  const request = useWorkspace((s) => s.reveal);
  useEffect(() => {
    if (request && refKey(request.ref) === refKey(ref)) setReveal({ id: request.key.split('.')[0]!, nonce: request.nonce, focus: request.focus, deletion: request.deletion });
  }, [request, ref]);

  const changeCount = diff?.changes.length ?? 0;
  const baseName = baseLabel(sketch);
  return (
    // Narrow (a phone): the JSON goes under the diagram instead of beside it.
    <div className="@container h-full" data-testid="sketch">
      <div className="flex h-full @max-[40rem]:flex-col">
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {readOnly && (
            <div className="flex flex-none items-center gap-2 border-b border-border bg-panel px-3 py-1.5 text-[12px] text-muted" role="note">
              <FolderOpen size={13} className="flex-none text-accent" />
              <span className="min-w-0 truncate">
                A file in the folder, written by an agent or by hand: <code className="font-mono text-text">{sketch.origin!.path}</code>. It updates when the file changes.
              </span>
              <button onClick={() => openSourceFile(sketch.origin!.path)} className="ml-auto flex flex-none items-center gap-1 rounded px-1.5 py-0.5 text-accent hover:bg-hover">
                <FileCode2 size={12} /> Open file
              </button>
              <button
                onClick={() =>
                  void duplicateSketch(id).then((copy) => {
                    if (copy) openSketch(copy.id);
                  })
                }
                className="flex flex-none items-center gap-1 rounded px-1.5 py-0.5 text-accent hover:bg-hover"
              >
                <Copy size={12} /> Duplicate to edit
              </button>
            </div>
          )}
          <div className="relative min-h-0 flex-1">
            {graph && positions ? (
              <Canvas
                graph={graph}
                positions={positions}
                selected={selected}
                onSelect={onSelect}
                onMoved={save}
                onRelayout={() => void relayout()}
                onConnect={readOnly ? undefined : onConnect}
                apiRef={canvas}
                fitKey={`sketch:${id}`}
                reveal={reveal}
                onFocusChange={setFocused}
                model={doc.model}
                status={
                  <>
                    {Object.keys(doc.model.entities).length} entities · {doc.model.relationships.length} relationships
                    {baseModel && <> · {changeCount} change{changeCount === 1 ? '' : 's'}</>}
                    {errors > 0 && <span className="text-removed"> · {errors} error{errors > 1 ? 's' : ''}</span>}
                  </>
                }
                tools={
                  <>
                    <Divider />
                    {!readOnly && (
                      <>
                        <ToolButton label="Add an entity" onClick={addNewEntity}>
                          <Plus size={15} />
                        </ToolButton>
                        <ToolButton label="Undo (Ctrl+Z)" disabled={!history?.past.length} onClick={() => undoSketch(id)}>
                          <Undo2 size={15} />
                        </ToolButton>
                        <ToolButton label="Redo (Ctrl+Y)" disabled={!history?.future.length} onClick={() => redoSketch(id)}>
                          <Redo2 size={15} />
                        </ToolButton>
                        <Divider />
                      </>
                    )}
                    <ToolButton
                      label={baseName ? (settings.sketchChanges ? `Hide changes from ${baseName}` : `Show changes from ${baseName}`) : 'This sketch has no base model to compare with'}
                      active={showChanges}
                      disabled={!baseRef}
                      onClick={settings.toggleSketchChanges}
                    >
                      <GitCompareArrows size={15} />
                    </ToolButton>
                    <ToolButton label={baseName ? `Compare side by side with ${baseName}` : 'No base model'} disabled={!baseRef} onClick={() => baseRef && openCompare([baseRef, ref])}>
                      <Columns2 size={15} />
                    </ToolButton>
                    {!readOnly && (
                      <ToolButton label={settings.sketchJson ? 'Hide the JSON' : 'Edit as JSON'} active={settings.sketchJson} onClick={settings.toggleSketchJson}>
                        <Braces size={15} />
                      </ToolButton>
                    )}
                    <ToolButton label="Download as .efmodel.json" onClick={() => exportSketch(getSketch(id)!)}>
                      <Download size={15} />
                    </ToolButton>
                  </>
                }
              />
            ) : (
              <div className="flex h-full items-center justify-center gap-2 text-muted">
                <Loader2 size={16} className="animate-spin" /> Opening sketch…
              </div>
            )}
          </div>
        </div>
        {settings.sketchJson && !readOnly && (
          <div className="w-[42%] max-w-[680px] min-w-[320px] flex-none border-l border-border @max-[40rem]:h-1/2 @max-[40rem]:w-auto @max-[40rem]:max-w-none @max-[40rem]:min-w-0 @max-[40rem]:border-t @max-[40rem]:border-l-0">
            <SketchJson sketchId={id} doc={doc} />
          </div>
        )}
      </div>
    </div>
  );
}

